import LockIcon from "@mui/icons-material/Lock";
import { Box, Button, Link, Typography } from "@mui/material";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../../services/AuthService";
import UtilService from "../../services/UtilService";
import Logger from "../../services/Logger";
import { usePagesTranslation } from "../locales";

const greenBtnSx = {
    backgroundColor: "#3BA666",
    "&:hover": { backgroundColor: "#2e7d32" }
};

const greenOutlinedSx = {
    color: "#7BDCA5",
    borderColor: "rgba(123, 220, 165, 0.7)",
    "&:hover": {
        borderColor: "#7BDCA5",
        backgroundColor: "rgba(59, 166, 102, 0.16)"
    }
};

/**
 * Light text on its own slate panel: the gate sits on the night play backdrop and,
 * when embedded, on the light how-to pages.
 */
const GATE_TITLE_COLOR = "#f4f8ff";
const GATE_BODY_COLOR = "rgba(226,232,240,0.82)";
const GATE_PANEL_SX = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: 620,
    width: "100%",
    px: { xs: 2.5, sm: 4 },
    py: { xs: 3.5, sm: 4.5 },
    borderRadius: 3,
    bgcolor: "rgba(15, 23, 42, 0.72)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(148, 163, 184, 0.28)",
    boxShadow: "0 18px 44px rgba(2, 6, 18, 0.35)"
};

/**
 * Shown when a paid (Standard) puzzle is accessed without permission.
 * - Not logged in: Signin page with create-account link.
 * - Logged in without Standard: prompt to upgrade (with optional go back).
 */
const PuzzleAccessGate = ({
    isAuthenticated,
    userPayPlan,
    onUpgradeClick,
    puzzleTitle,
    onBackClick
}) => {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const hasStandardPlan = UtilService.isStandardPayPlan(userPayPlan);

    const handleLogin = () => {
        navigate("/login", { state: { from: { pathname: location.pathname } } });
    };

    const handleSignUp = async () => {
        try {
            await AuthService.signup();
        } catch (error) {
            Logger.error("Error redirecting to signup:", error);
        }
    };

    const handleUpgrade = () => {
        if (onUpgradeClick) onUpgradeClick();
    };

    const premiumLine = puzzleTitle
        ? t("core.puzzleAccess.premiumPuzzle", { title: puzzleTitle })
        : t("core.puzzleAccess.premiumPuzzleDefault");

    if (!isAuthenticated) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "60vh",
                    px: 2,
                    py: 4,
                    textAlign: "center"
                }}
            >
                <Box sx={GATE_PANEL_SX}>
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            maxWidth: 560,
                            color: GATE_TITLE_COLOR,
                            lineHeight: 1.3
                        }}
                    >
                        {premiumLine}
                    </Typography>
                    <Typography variant="body2" sx={{ maxWidth: 420, mb: 3, color: GATE_BODY_COLOR }}>
                        {t("core.puzzleAccess.signInBody")}
                    </Typography>
                    <Button variant="contained" size="large" onClick={handleLogin} sx={{ ...greenBtnSx, mb: 2.5, px: 4 }}>
                        {t("core.puzzleAccess.signIn")}
                    </Button>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5
                        }}
                    >
                        <Typography component="span" variant="body2" sx={{ color: GATE_BODY_COLOR }}>
                            {t("core.puzzleAccess.noAccount")}
                        </Typography>
                        <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={handleSignUp}
                            underline="hover"
                            sx={{
                                cursor: "pointer",
                                fontWeight: 600,
                                color: "#7BDCA5",
                                verticalAlign: "baseline"
                            }}
                        >
                            {t("core.puzzleAccess.createAccount")}
                        </Link>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "60vh",
                px: 2,
                py: 4,
                textAlign: "center"
            }}
        >
            <Box sx={GATE_PANEL_SX}>
                <LockIcon sx={{ fontSize: 64, color: "#7BDCA5", mb: 2 }} />
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600, color: GATE_TITLE_COLOR }}
                >
                    {premiumLine}
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 400, mb: 3, color: GATE_BODY_COLOR }}>
                    {!hasStandardPlan ? t("core.puzzleAccess.upgradePrompt") : null}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "center" }}>
                    {onBackClick ? (
                        <Button variant="outlined" onClick={onBackClick} sx={greenOutlinedSx}>
                            {t("core.puzzleAccess.goBack")}
                        </Button>
                    ) : null}
                    {!hasStandardPlan && onUpgradeClick ? (
                        <Button variant="contained" onClick={handleUpgrade} sx={greenBtnSx}>
                            {t("core.puzzleAccess.upgradeToPremium")}
                        </Button>
                    ) : null}
                </Box>
            </Box>
        </Box>
    );
};

export default PuzzleAccessGate;
